'use client';
import React, { useState, useEffect, useRef } from 'react';
import IOSInstallPrompt from '@/components/ui/IOSInstallPrompt';

// ─── Types ───────────────────────────────────────────────────────────────────
interface FAQItem {
  question: string;
  answer: string;
}

interface Testimonial {
  name: string;
  role: string;
  quote: string;
  initials: string;
  gradient: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const faqs: FAQItem[] = [
{
  question: 'Is Checkout Genie really free to try?',
  answer:
  'Yes! Checkout Genie comes with a free trial so you can experience every feature before committing. After the trial, it\'s just $2.99/month — less than a cup of coffee — to keep saving on every purchase.'
},
{
  question: 'How does Checkout Genie find and apply coupon codes?',
  answer:
  'Our AI continuously indexes verified coupon codes from thousands of trusted sources. When you reach checkout on any supported store, Checkout Genie silently tests every available code in seconds and auto-applies the one that saves you the most. No copy-pasting, no hunting.'
},
{
  question: 'Does Checkout Genie use affiliate links or sell my data?',
  answer:
  'Absolutely not. Checkout Genie earns revenue only from subscriptions — never from affiliate commissions, tracking cookies, or selling your browsing data. We have zero financial incentive to show you anything other than the best deal.'
},
{
  question: 'What is the Price Drop Alert feature?',
  answer:
  'Spot a product you want but the price is too high? Click the Checkout Genie icon and set a target price. We\'ll monitor it 24/7 and send you an email the moment the price drops to your target — so you never miss a sale.'
},
{
  question: 'What is the Better Deal Finder?',
  answer:
  'While you browse, Checkout Genie scans competing retailers in real-time to check if the exact same product is available for less elsewhere. If it finds a better price, a subtle notification appears so you can decide whether to switch — no pressure, just information.'
},
{
  question: 'Will Checkout Genie slow down my browser?',
  answer:
  'No. Checkout Genie is engineered to be ultra-lightweight. It only activates on checkout pages and product pages you\'re actively viewing. Background scanning is throttled so it never competes with your browsing experience.'
}];


const testimonials: Testimonial[] = [
{
  name: 'Amanda K.',
  role: 'Beta Tester · Seattle, WA',
  quote:
  '"Checkout Genie found a 20% off code I had no idea existed and applied it automatically. I literally did nothing. This is the extension I\'ve always wanted."',
  initials: 'AK',
  gradient: 'from-teal-500 to-cyan-600'
},
{
  name: 'Marcus T.',
  role: 'Beta Tester · Austin, TX',
  quote:
  '"The price drop alert saved me $43 on headphones I\'d been watching for weeks. Got the email, clicked the link, bought them. Effortless."',
  initials: 'MT',
  gradient: 'from-blue-500 to-indigo-600'
},
{
  name: 'Priya S.',
  role: 'Beta Tester · Chicago, IL',
  quote:
  '"I love that there are zero affiliate cookies. I trust the deals because I know Checkout Genie isn\'t steering me toward higher-commission products. It\'s genuinely on my side."',
  initials: 'PS',
  gradient: 'from-purple-500 to-pink-600'
},
{
  name: 'Jordan L.',
  role: 'Beta Tester · New York, NY',
  quote:
  '"The Better Deal Finder is a game-changer. It quietly told me the same laptop was $80 cheaper on a different site. Saved more than a year\'s subscription in one purchase."',
  initials: 'JL',
  gradient: 'from-teal-500 to-cyan-600'
},
{
  name: 'Rachel M.',
  role: 'Beta Tester · Denver, CO',
  quote:
  '"$2.99 a month is nothing compared to what I save. I\'ve already saved over $200 in the beta alone. This extension pays for itself every single time I shop."',
  initials: 'RM',
  gradient: 'from-blue-500 to-indigo-600'
},
{
  name: 'Chris W.',
  role: 'Beta Tester · Portland, OR',
  quote:
  '"Finally an extension that doesn\'t feel sketchy. No ads, no affiliate nonsense — just pure savings. The AI actually works and the UI is clean as heck."',
  initials: 'CW',
  gradient: 'from-purple-500 to-pink-600'
}];


const stores = [
'Amazon', 'Target', 'Walmart', 'Best Buy', 'Nike', 'Adidas',
'eBay', 'Etsy', 'Nordstrom', 'Sephora', 'Home Depot', 'Wayfair',
'Amazon', 'Target', 'Walmart', 'Best Buy', 'Nike', 'Adidas',
'eBay', 'Etsy', 'Nordstrom', 'Sephora', 'Home Depot', 'Wayfair'];


// ─── Sub-components ──────────────────────────────────────────────────────────
function StarRating({ count = 5 }: {count?: number;}) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: count }).map((_, i) =>
      <svg key={i} className="text-yellow-400 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )}
    </div>);

}

// ─── Feature Carousel ────────────────────────────────────────────────────────
const carouselSlides = [
{
  id: 0,
  gradient: 'from-teal-500 to-cyan-600',
  bgGradient: 'from-teal-50 to-cyan-50',
  borderColor: 'border-teal-400',
  titleColor: 'text-teal-900',
  descColor: 'text-teal-700',
  accentColor: 'text-teal-600',
  badgeBg: 'bg-teal-100',
  badgeText: 'text-teal-700',
  dotActive: 'bg-teal-500',
  icon:
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />,

  badge: 'Auto-Applied',
  title: 'Auto-Apply Coupon Codes',
  desc: 'Checkout Genie tests every verified code in seconds and applies the biggest discount automatically — no copy-pasting required.',
  highlight: 'Code "DEAL20" applied',
  highlightSub: 'You saved $27.60 instantly',
  stat: '$27.60',
  statLabel: 'Saved automatically'
},
{
  id: 1,
  gradient: 'from-blue-500 to-indigo-600',
  bgGradient: 'from-blue-50 to-indigo-50',
  borderColor: 'border-blue-400',
  titleColor: 'text-blue-900',
  descColor: 'text-blue-700',
  accentColor: 'text-blue-600',
  badgeBg: 'bg-blue-100',
  badgeText: 'text-blue-700',
  dotActive: 'bg-blue-500',
  icon:
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,

  badge: 'Real-Time Scan',
  title: 'Better Deal Finder',
  desc: 'Scans competing retailers in real-time to surface the same product at a lower price — zero affiliate bias, ever.',
  highlight: 'Found same item for $94.99',
  highlightSub: 'At BestBuy — $35 cheaper',
  stat: '$35.00',
  statLabel: 'Cheaper elsewhere'
},
{
  id: 2,
  gradient: 'from-purple-500 to-pink-600',
  bgGradient: 'from-purple-50 to-pink-50',
  borderColor: 'border-purple-400',
  titleColor: 'text-purple-900',
  descColor: 'text-purple-700',
  accentColor: 'text-purple-600',
  badgeBg: 'bg-purple-100',
  badgeText: 'text-purple-700',
  dotActive: 'bg-purple-500',
  icon:
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />,

  badge: '24/7 Monitoring',
  title: 'Price Drop Alerts',
  desc: 'Set a target price on any product. We monitor it 24/7 and email you the instant it drops — so you never miss a sale.',
  highlight: '🎉 Price dropped to $89.99!',
  highlightSub: 'Your target was $95 — alert sent',
  stat: '$89.99',
  statLabel: 'Hit your target price'
}];


function FeatureCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % carouselSlides.length);
    }, 3200);
    return () => clearInterval(timer);
  }, [paused]);

  const slide = carouselSlides[active];

  return (
    <div
      className="mb-4"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}>
      
      {/* Slide */}
      <div
        key={active}
        className={`bg-gradient-to-br ${slide.bgGradient} border-2 ${slide.borderColor} rounded-2xl p-5 transition-all duration-500`}
        style={{ minHeight: '200px' }}>
        
        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-9 h-9 bg-gradient-to-br ${slide.gradient} rounded-xl flex items-center justify-center shrink-0 shadow-md`}>
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {slide.icon}
            </svg>
          </div>
          <div className="flex-1">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${slide.badgeBg} ${slide.badgeText}`}>
              {slide.badge}
            </span>
            <h3 className={`font-bold text-sm mt-0.5 ${slide.titleColor}`}>{slide.title}</h3>
          </div>
        </div>

        {/* Description */}
        <p className={`text-xs leading-relaxed mb-4 ${slide.descColor}`}>{slide.desc}</p>

        {/* Highlight box */}
        <div className={`bg-white/70 rounded-xl px-4 py-3 flex items-center justify-between`}>
          <div>
            <p className={`text-xs font-semibold ${slide.titleColor}`}>{slide.highlight}</p>
            <p className={`text-xs ${slide.descColor} mt-0.5`}>{slide.highlightSub}</p>
          </div>
          <div className="text-right">
            <p className={`text-2xl font-extrabold ${slide.accentColor}`}>{slide.stat}</p>
            <p className={`text-xs ${slide.descColor}`}>{slide.statLabel}</p>
          </div>
        </div>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-3">
        {carouselSlides.map((s, i) =>
        <button
          key={i}
          onClick={() => {setActive(i);setPaused(true);}}
          className={`h-2 rounded-full transition-all duration-300 ${
          i === active ? `w-6 ${slide.dotActive}` : 'w-2 bg-slate-300'}`
          }
          aria-label={`Show ${s.title}`} />

        )}
      </div>
    </div>);

}

function FAQAccordion({ items }: {items: FAQItem[];}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return (
    <div className="space-y-4">
      {items.map((item, i) =>
      <div key={i} className="bg-white rounded-2xl border border-sky-100 overflow-hidden">
          <button
          className="flex items-center justify-between w-full p-6 text-left text-slate-900 font-semibold cursor-pointer"
          onClick={() => setOpenIndex(openIndex === i ? null : i)}>
          
            <span>{item.question}</span>
            <svg
            className={`w-5 h-5 transition-transform text-slate-400 shrink-0 ml-4 ${
            openIndex === i ? 'rotate-180' : ''}`
            }
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div
          className={`grid transition-all duration-300 ${
          openIndex === i ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`
          }>
          
            <div className="overflow-hidden">
              <div className="px-6 pb-6 text-slate-600 leading-relaxed">{item.answer}</div>
            </div>
          </div>
        </div>
      )}
    </div>);

}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function HomePage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const revealRefs = useRef<(HTMLElement | null)[]>([]);

  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setSubmitted(true);
    }
  };

  return (
    <div className="font-sans">
      <IOSInstallPrompt />
      {/* ── NAV ── */}
      <nav className="sticky -top-px z-50 w-full bg-white/95 backdrop-blur-sm border-b border-sky-100 py-4">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-5">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 group">
            <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg shadow-teal-500/30">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <span className="font-bold text-xl tracking-tight text-slate-900 group-hover:text-teal-600 transition-colors">
              Checkout Genie
            </span>
          </a>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-5 md:gap-8 text-sm font-medium text-slate-600">
            {['Features', 'How It Works', 'Pricing', 'FAQ'].map((item) =>
            <a
              key={item}
              href={`#${item.toLowerCase().replace(/ /g, '-')}`}
              className="hover:text-teal-600 transition-colors">
              
                {item}
              </a>
            )}
          </div>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="#early-access"
              className="inline-flex items-center justify-center px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-cyan-600 rounded-full hover:shadow-lg hover:shadow-teal-500/30 transition-all">
              
              Get Early Access
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden text-slate-900 p-2"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Toggle menu">
            
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile menu backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[60]"
          style={{ backgroundColor: '#000000' }}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile menu drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-72 bg-slate-900 z-[70] flex flex-col pt-6 px-6 pb-12 shadow-2xl transition-transform duration-300 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
        
        <div className="flex items-center justify-between mb-8">
          <span className="text-white font-semibold text-lg">Menu</span>
          <button
            className="text-white p-1"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col gap-1">
          {['Features', 'How It Works', 'Pricing', 'FAQ'].map((item) => (
            <a
              key={item}
              href={`#${item.toLowerCase().replace(/ /g, '-')}`}
              onClick={() => setMobileMenuOpen(false)}
              className="py-3 border-b border-white/10 text-white font-medium text-base hover:text-white/70 transition-colors">
              {item}
            </a>
          ))}
          <a
            href="#early-access"
            onClick={() => setMobileMenuOpen(false)}
            className="mt-6 inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-slate-900 bg-white rounded-full hover:bg-white/90 transition-colors">
            Get Early Access
          </a>
        </div>
      </div>

      {/* ── HERO ── */}
      <div className="flex flex-col overflow-hidden w-full pt-16 pb-32 relative items-center">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            alt="Person shopping online on laptop with shopping bags and packages around them"
            className="w-full h-full object-cover"
            src="https://img.rocket.new/generatedImages/rocket_gen_img_18e9ecb7f-1773156449656.png" />
          
          <div className="bg-gradient-to-br from-slate-900/75 via-blue-900/65 to-teal-900/55 absolute inset-0" />
        </div>

        <div className="z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 w-full max-w-7xl mx-auto px-6 relative mb-20">
          {/* Left: headline */}
          <div className="flex flex-col max-w-2xl justify-center">
            {/* Badge */}
            <div className="inline-flex self-start px-4 py-2 mb-6 border border-teal-400/30 rounded-full bg-teal-500/10 backdrop-blur-sm">
              <span className="text-xs font-semibold tracking-wide text-teal-300 uppercase flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-400" />
                </span>
                Beta Now Open · Free Trial Included
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight leading-[1.1] mb-6">
              <span className="reveal-text overflow-hidden block">
                <span className="reveal-inner mb-2">AI that saves you</span>
              </span>
              <span className="reveal-text overflow-hidden block">
                <span className="reveal-inner mb-2">money at checkout.</span>
              </span>
            </h1>

            <p className="text-xl leading-relaxed text-sky-100 max-w-lg mb-10">
              Checkout Genie auto-applies verified coupon codes, finds better prices across the web, and alerts you when prices drop — all inside Chrome. No ads. No affiliate cookies. Ever.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#early-access"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-slate-900 bg-white rounded-full hover:shadow-2xl hover:shadow-white/20 transition-all shadow-xl">
                
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
                Get Early Access — Free
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-transparent border-2 border-white/30 rounded-full hover:bg-white/10 backdrop-blur-sm transition-all">
                
                See How It Works
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </a>
            </div>

            {/* Social proof pills */}
            <div className="flex items-center gap-6 mt-8">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="text-sm text-sky-100">Loved by beta testers</span>
              </div>
              <div className="h-4 w-px bg-white/20" />
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-sm text-sky-100">Zero affiliate cookies</span>
              </div>
            </div>
          </div>

          {/* Right: stats card */}
          <div className="mx-auto w-full reveal-on-scroll">
            <div className="bg-white/95 backdrop-blur-md rounded-4xl shadow-2xl shadow-slate-900/20 p-6 md:p-8 flex flex-col md:flex-row gap-6 md:gap-10 items-start md:items-center border border-sky-100 w-full md:w-fit">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg shadow-teal-500/30">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Avg. Savings / User</p>
                  <p className="text-xl font-bold text-slate-900">$18.40 / order</p>
                </div>
              </div>
              <div className="w-full h-px md:w-px md:h-12 bg-sky-200" />
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Codes Verified</p>
                  <p className="text-xl font-bold text-slate-900">500K+</p>
                </div>
              </div>
              <div className="w-full h-px md:w-px md:h-12 bg-sky-200" />
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Success Rate</p>
                  <p className="text-xl font-bold text-slate-900">96%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            {/* Left: copy */}
            <div className="lg:w-1/2">
              <span className="text-xs font-semibold tracking-wider text-teal-600 uppercase mb-3 block">
                Automatic Savings
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-6">
                AI that works while you shop
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Install Checkout Genie once and forget about it. Our AI runs silently in the background — testing codes, comparing prices, and watching for drops — so you always pay less without lifting a finger.
              </p>

              <div className="space-y-4 mb-10">
                {[
                {
                  icon:
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />,

                  gradient: 'from-teal-500 to-cyan-600',
                  shadow: 'shadow-teal-500/20',
                  title: 'Auto-Apply Coupon Codes',
                  desc: 'At checkout, Checkout Genie tests every verified code in seconds and applies the biggest discount automatically.'
                },
                {
                  icon:
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,

                  gradient: 'from-blue-500 to-indigo-600',
                  shadow: 'shadow-blue-500/20',
                  title: 'Better Deal Finder',
                  desc: 'Scans competing retailers in real-time to surface the same product at a lower price — no affiliate bias, ever.'
                },
                {
                  icon:
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />,

                  gradient: 'from-purple-500 to-pink-600',
                  shadow: 'shadow-purple-500/20',
                  title: 'Price Drop Alerts',
                  desc: 'Set a target price on any product. We monitor it 24/7 and email you the instant it drops — so you never miss a sale.'
                }].
                map((feature, i) =>
                <div key={i} className="flex items-start gap-4 p-4 rounded-2xl hover:bg-sky-50 transition-colors">
                    <div
                    className={`w-10 h-10 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center shrink-0 shadow-lg ${feature.shadow}`}>
                    
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {feature.icon}
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">{feature.title}</h3>
                      <p className="text-sm text-slate-600">{feature.desc}</p>
                    </div>
                  </div>
                )}
              </div>

              <a
                href="#early-access"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-gradient-to-r from-teal-500 to-cyan-600 rounded-full hover:shadow-xl hover:shadow-teal-500/30 transition-all">
                
                Get Early Access — Free
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
            </div>

            {/* Right: browser mockup */}
            <div className="lg:w-1/2 reveal-on-scroll">
              <div className="browser-mockup rounded-4xl overflow-hidden shadow-2xl shadow-slate-900/20 border border-slate-200">
                {/* Browser chrome */}
                <div className="h-12 bg-slate-100 flex items-center px-4 border-b border-slate-200 gap-2">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="bg-white px-4 py-1.5 rounded-lg text-xs text-slate-500 font-mono">
                      amazon.com/checkout
                    </div>
                  </div>
                </div>

                {/* Checkout UI */}
                <div className="relative bg-white p-8">
                  {/* Feature Carousel */}
                  <FeatureCarousel />

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES / TRUST ── */}
      <section id="features" className="py-24 bg-sky-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 reveal-on-scroll">
            <span className="text-xs font-semibold tracking-wider text-teal-600 uppercase mb-3 block">
              Why Checkout Genie is Different
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              Built for shoppers, not advertisers
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Most coupon extensions make money by steering you toward higher-commission products. Checkout Genie earns only from subscriptions — so every recommendation is 100% unbiased.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
            {
              gradient: 'from-teal-500 to-cyan-600',
              shadow: 'shadow-teal-500/30',
              icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />,
              title: 'Zero Ads & Affiliate Cookies',
              desc: 'We never inject affiliate tracking cookies or earn commissions from stores. Our only revenue is your $2.99/month subscription.',
              badge: 'No Affiliate Revenue',
              badgeIcon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            },
            {
              gradient: 'from-blue-500 to-indigo-600',
              shadow: 'shadow-blue-500/30',
              icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />,
              title: 'Privacy-First Architecture',
              desc: 'We never store your payment info, browsing history, or purchase data. What you buy stays between you and the store.',
              badge: 'GDPR Compliant',
              badgeIcon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            },
            {
              gradient: 'from-purple-500 to-pink-600',
              shadow: 'shadow-purple-500/30',
              icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />,
              title: 'AI-Verified Codes Only',
              desc: 'Our AI validates every coupon code before showing it to you. No expired codes, no fake discounts — a 96% success rate on every checkout.',
              badge: '96% Success Rate',
              badgeIcon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            }].
            map((card, i) =>
            <div
              key={i}
              className="reveal-on-scroll bg-white rounded-4xl p-8 border border-sky-100 hover:shadow-2xl hover:shadow-slate-900/10 transition-all duration-300 flex flex-col">
              
                <div
                className={`w-14 h-14 bg-gradient-to-br ${card.gradient} rounded-2xl flex items-center justify-center mb-6 shadow-lg ${card.shadow}`}>
                
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {card.icon}
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{card.title}</h3>
                <p className="text-slate-600 leading-relaxed mb-4 flex-1">{card.desc}</p>
                <div className="flex items-center gap-2 text-sm text-teal-600 font-medium">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {card.badgeIcon}
                  </svg>
                  {card.badge}
                </div>
              </div>
            )}
          </div>

          {/* Featured testimonial */}
          <div className="mt-16 reveal-on-scroll">
            <div className="bg-white rounded-4xl p-8 md:p-12 border border-sky-100 shadow-xl shadow-slate-900/5">
              <StarRating />
              <blockquote className="text-2xl font-medium text-slate-900 mt-4 mb-6 leading-relaxed">
                &quot;I switched from a free coupon extension that was stuffing affiliate cookies into every link. Checkout Genie is the first one I actually trust. It found me a 22% off code on my first use and I didn&apos;t have to do a thing.&quot;
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  BN
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Brianna N.</p>
                  <p className="text-sm text-slate-600">Beta Tester · San Francisco, CA</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STORES MARQUEE ── */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 reveal-on-scroll">
            <span className="text-xs font-semibold tracking-wider text-teal-600 uppercase mb-3 block">
              Works Everywhere
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              10,000+ stores supported
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              From Amazon to Zappos, Checkout Genie works on all your favorite online stores — and we&apos;re adding more every week.
            </p>
          </div>

          {/* Marquee */}
          <div className="relative mb-12">
            <div className="marquee-container py-8">
              <div className="animate-marquee">
                {stores.map((store, i) =>
                <div
                  key={i}
                  className="bg-white border border-slate-200 rounded-2xl px-8 py-6 shadow-sm hover:shadow-lg transition-shadow shrink-0">
                  
                    <span className="text-2xl font-bold text-slate-900">{store}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 reveal-on-scroll">
            {[
            { value: '10,000+', label: 'Supported Stores' },
            { value: '500K+', label: 'Codes Verified' },
            { value: '96%', label: 'Coupon Success Rate' }].
            map((stat, i) =>
            <div
              key={i}
              className="text-center p-8 rounded-4xl bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-100">
              
                <div className="text-5xl font-bold text-teal-600 mb-2">{stat.value}</div>
                <p className="text-slate-600 font-medium">{stat.label}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-24 bg-sky-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-16 reveal-on-scroll">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              Frequently asked questions
            </h2>
            <p className="text-xl text-slate-600">Everything you need to know about Checkout Genie</p>
          </div>
          <FAQAccordion items={faqs} />
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 reveal-on-scroll">
            <span className="text-xs font-semibold tracking-wider text-teal-600 uppercase mb-3 block">
              Simple Pricing
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              One plan. All features.
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Start with a free trial. No credit card required. Upgrade for less than a cup of coffee.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Trial */}
            <div className="reveal-on-scroll bg-white text-slate-700 border-sky-100 rounded-4xl p-8 border-2 hover:shadow-xl hover:shadow-slate-900/10 transition-all duration-300">
              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-2">Free Trial</h3>
                <p className="text-slate-600 text-sm">Try every feature, no card needed</p>
              </div>
              <div className="mb-8">
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-bold">$0</span>
                  <span className="text-slate-500">/ trial period</span>
                </div>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                'Auto-apply coupon codes',
                'Better Deal Finder',
                'Price Drop Alerts',
                '10,000+ stores',
                'No credit card required'].
                map((f) =>
                <li key={f} className="flex items-center gap-3 text-sm text-slate-700">
                    <svg className="w-5 h-5 text-teal-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {f}
                  </li>
                )}
              </ul>
              <a
                href="#early-access"
                className="block w-full text-center px-6 py-3 text-base font-semibold text-teal-600 border-2 border-teal-600 rounded-full hover:bg-teal-50 transition-all duration-300">
                
                Start Free Trial
              </a>
            </div>

            {/* Pro */}
            <div className="reveal-on-scroll relative bg-gradient-to-br from-teal-500 to-cyan-600 text-white rounded-4xl p-8 border-2 border-teal-500 hover:shadow-xl hover:shadow-teal-500/30 transition-all duration-300">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-white text-teal-600 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-lg">
                Most Popular
              </div>
              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-2">Checkout Genie Pro</h3>
                <p className="text-teal-100 text-sm">All features, cancel anytime</p>
              </div>
              <div className="mb-8">
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-bold">$2.99</span>
                  <span className="text-teal-100">/ month</span>
                </div>
                <p className="text-teal-200 text-xs mt-1">That&apos;s less than $0.10 per day</p>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                'Everything in Free Trial',
                'Unlimited price drop alerts',
                'Priority code database updates',
                'Multi-device sync',
                'Email savings reports',
                'Zero ads. Zero affiliate cookies.'].
                map((f) =>
                <li key={f} className="flex items-center gap-3 text-sm text-white">
                    <svg className="w-5 h-5 text-teal-200 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {f}
                  </li>
                )}
              </ul>
              <a
                href="#early-access"
                className="block w-full text-center px-6 py-3 bg-white text-base font-semibold text-teal-600 rounded-full hover:bg-teal-50 transition-all duration-300">
                
                Get Early Access
              </a>
            </div>
          </div>

          <div className="mt-12 text-center reveal-on-scroll">
            <div className="inline-flex items-center gap-3 px-6 py-4 bg-sky-50 border border-sky-200 rounded-2xl">
              <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-slate-700 font-medium">30-day money-back guarantee · Cancel anytime · No hidden fees</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-24 bg-sky-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-wider text-teal-600 uppercase mb-3 block">
              Beta Tester Love
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              What early users are saying
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Real feedback from our beta community — no incentivized reviews, no fake testimonials.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((t, i) =>
            <div
              key={i}
              className="reveal-on-scroll bg-white rounded-4xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              
                <StarRating />
                <p className="text-slate-700 my-4 leading-relaxed">{t.quote}</p>
                <div className="flex items-center gap-3">
                  <div
                  className={`w-12 h-12 bg-gradient-to-br ${t.gradient} rounded-full flex items-center justify-center text-white font-bold`}>
                  
                    {t.initials}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-900">{t.name}</p>
                      <svg className="w-5 h-5 text-teal-600" fill="currentColor" viewBox="0 0 20 20">
                        <path
                        fillRule="evenodd"
                        d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd" />
                      
                      </svg>
                    </div>
                    <p className="text-sm text-slate-500">{t.role}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── EARLY ACCESS / EMAIL CAPTURE ── */}
      <section
        id="early-access"
        className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 relative overflow-hidden">
        
        {/* Decorative blobs */}
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-400/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl" />

        <div className="relative max-w-2xl mx-auto px-6 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 border border-teal-400/30 rounded-full bg-teal-500/10 backdrop-blur-sm">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-400" />
            </span>
            <span className="text-xs font-semibold tracking-wide text-teal-300 uppercase">
              Beta Access · Free Trial Included
            </span>
          </div>

          <h2 className="text-5xl md:text-6xl font-bold text-white tracking-tight mb-6 leading-[1.1]">
            Be first to save
            <br />
            smarter.
          </h2>

          <p className="text-xl text-sky-100 mb-10 max-w-xl mx-auto leading-relaxed">
            Join the Checkout Genie beta waitlist. Get early access, a free trial, and lock in your spot before we launch publicly. No spam — just your invite.
          </p>

          {!submitted ?
          <form onSubmit={handleSubmit} suppressHydrationWarning className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-8">
              <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              className="flex-1 px-5 py-4 rounded-full bg-white/10 border border-white/20 text-white placeholder-sky-300 focus:outline-none focus:ring-2 focus:ring-teal-400 backdrop-blur-sm text-sm" />
            
              <button
              type="submit"
              className="px-8 py-4 bg-white text-slate-900 font-bold rounded-full hover:shadow-2xl hover:shadow-white/20 transition-all text-sm whitespace-nowrap">
              
                Get Early Access
              </button>
            </form> :

          <div className="flex flex-col items-center gap-4 mb-8">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-xl font-semibold text-white">You&apos;re on the list!</p>
              <p className="text-sky-200 text-sm">We&apos;ll send your invite to <strong className="text-white">{email}</strong> soon.</p>
            </div>
          }

          <p className="text-sky-300 text-xs mb-10">
            No credit card required · Unsubscribe anytime · We never sell your email
          </p>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center items-center gap-8 text-sky-200">
            <div className="flex items-center gap-2">
              <svg className="text-yellow-400 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="text-sm">Loved by Beta Testers</span>
            </div>
            <div className="h-4 w-px bg-white/20" />
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              <span className="text-sm">Zero Affiliate Cookies</span>
            </div>
            <div className="h-4 w-px bg-white/20" />
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-sm">Privacy First</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── FLOATING CTA ── */}
      <div className="fixed bottom-6 right-6 z-20 float-animation">
        <a
          href="#early-access"
          aria-label="Get early access"
          className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-600 text-white rounded-full flex items-center justify-center shadow-2xl shadow-teal-500/40 hover:scale-110 transition-transform duration-300">
          
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
          </svg>
        </a>
      </div>

      {/* ── FOOTER ── */}
      <footer className="bg-white py-12 border-t border-sky-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <span className="font-bold text-lg text-slate-900">Checkout Genie</span>
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-600">
              {['Privacy Policy', 'Terms of Service', 'Contact'].map((link) =>
              <a key={link} href="#" className="hover:text-teal-600 transition-colors">
                  {link}
                </a>
              )}
            </div>

            <p className="text-sm text-slate-500">© 2025 Checkout Genie. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>);

}